import * as React from 'react';
import { useEffect } from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { createStyles, makeStyles } from '@material-ui/core';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import ReportsFilter from './ReportsFilter';
import ReportsList from './ReportsList';
import { ReportFilter } from '../../Interfaces/Reports/ReportFilter';
import {
  fetchReportDataByNode,
  fetchReportDataByStore,
  setPanelBarVisibility,
  resetFetchError,
} from '../../store/Reports/ReportsActions';
import { ReportQueryInfoByNode } from '../../Interfaces/Reports/ReportQueryInfoByNode';
import { ReportQueryInfoByStore } from '../../Interfaces/Reports/ReportQueryInfoByStore';
import { ReportsActionTypes } from '../../store/Reports/ReportsState';
import { ReportQueryResult } from '../../Interfaces/Reports/ReportQueryResult';
import { ApplicationState } from '../../store';
import NavigationNextIcon from '@mui/icons-material/NavigateNext';
import StringResourceManager, { GetString } from '../StringResourceManager';
import {
  Breadcrumb,
  BreadcrumbLinkMouseEvent,
} from '@progress/kendo-react-layout';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import { getDateRange } from '../../utils/utils';
import { ReportTitleInfo } from '../../Interfaces/Reports/ReportTitleInfo';
import { Dialog } from '@progress/kendo-react-dialogs';
import ReportExportStatusesGrid from './ReportExportStatusesGrid';
import { Button } from '@progress/kendo-react-buttons';

const useStyles = makeStyles(theme =>
  createStyles({
    reportsFilter: {
      margin: '20px',
    },
    reportsList: {
      margin: '20px',
      border: '1px solid black',
      maxHeight: '70vh',
      overflowY: 'scroll',
    },
    header: {
      margin: '10px',
      fontSize: '1.5rem',
      color: '#23496B',
      fontWeight: 'bold',
    },
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    error: {
      fontSize: '1.5rem',
    },
    headerControlsContainer: {
      marginLeft: '20px',
    },
  }),
);

interface Report {
  id?: string;
  text?: string;
}

const ReportsSelection: React.FC = (): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const status = GetString('STATUS');

  const reportFilter: ReportFilter = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.reportFilter,
  );

  const fetchError: any = useSelector(
    (state: ApplicationState) => state.reportsReducer.fetchError,
  );

  const [error, displayError] = React.useState(false);

  const [isReportsPanelBarVisible, setIsReportsPanelBarVisible] =
    React.useState<boolean>(false);

  const [ReportExportsGridModalVisible, setReportExportsGridModalVisible] =
    React.useState<boolean>(false);

  const PAGE_TITLE_REPORTS = GetString('PAGE_TITLE_REPORTS');
  const items: Report[] = [
    {
      id: PAGE_TITLE_REPORTS,
      text: PAGE_TITLE_REPORTS,
    },
  ];
  const [breadcrumbData, setBreadcrumbData] = React.useState<Report[]>(items);

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  const [reportTitle, setReportTitle] = React.useState<ReportTitleInfo>();

  useEffect(() => {
    if (breadcrumbData.length > 1) {
      processFetchReportData(reportTitle?.ReportID);

      setBreadcrumbData([
        {
          id: 'reports',
          text: PAGE_TITLE_REPORTS,
        },
        {
          id: reportTitle?.ReportID,
          text:
            selectedLanguage === 'English'
              ? reportTitle?.ReportTitle.English
              : reportTitle?.ReportTitle.French,
        },
      ]);
    }
  }, [selectedLanguage]);

  const processFetchReportData = (reportId?: string) => {
    let range = getDateRange(reportFilter.DateRange);
    if (
      reportFilter.SiteNumbers.length > 1 ||
      reportFilter.LaneNumbers.length === 0
    ) {
      let query: ReportQueryInfoByStore = {
        ReportID: reportId,
        SiteNumbers: reportFilter.SiteNumbers,
        StartDateTime: range ? range[0] : reportFilter.StartDateTime,
        EndDateTime: range ? range[1] : reportFilter.EndDateTime,
        Language: selectedLanguage,
      };
      fetchReportDataByStore(dispatch, query);
    } else {
      let query: ReportQueryInfoByNode = {
        ReportID: reportId,
        SiteNumber: reportFilter.SiteNumbers[0],
        LaneNumbers: reportFilter.LaneNumbers,
        StartDateTime: range ? range[0] : reportFilter.StartDateTime,
        EndDateTime: range ? range[1] : reportFilter.EndDateTime,
        Language: selectedLanguage,
      };
      fetchReportDataByNode(dispatch, query);
    }

    setPanelBarVisibility(dispatch, false);
  };

  const handleGenerateReportClick = async (
    reportTitleInfo: ReportTitleInfo,
  ) => {
    processFetchReportData(reportTitleInfo.ReportID);

    setReportTitle(reportTitleInfo);

    setBreadcrumbData([
      {
        id: 'reports',
        text: PAGE_TITLE_REPORTS,
      },
      {
        id: reportTitleInfo.ReportID,
        text:
          selectedLanguage === 'English'
            ? reportTitleInfo.ReportTitle.English
            : reportTitleInfo.ReportTitle.French,
      },
    ]);
  };

  const handleItemSelect = (event: BreadcrumbLinkMouseEvent) => {
    if (event.id === 'reports') {
      setPanelBarVisibility(dispatch, true);

      returnToReportsList();
    }
  };

  const returnToReportsList = () => {
    if (breadcrumbData.length == 1) return;

    setBreadcrumbData(items);

    dispatch({
      type: ReportsActionTypes.RECEIVED_REPORT_DATA,
      queryResult: {} as ReportQueryResult,
    });
  };

  const setReportsPanelBarVisibility = (bool: boolean) => {
    setIsReportsPanelBarVisible(bool);
  };

  React.useEffect(() => {
    returnToReportsList();
  }, [reportFilter]);

  useEffect(() => {
    let timeoutId: any;
    if (fetchError.StatusCode > 0 && fetchError.Message != '') {
      displayError(true);
      timeoutId = setTimeout(() => {
        clearFetchError();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchError]);

  const clearFetchError = () => {
    displayError(false);
    resetFetchError(dispatch);
  };

  const isReportPageChange = () => {
    dispatch({
      type: ReportsActionTypes.RECEIVED_REPORT_DATA,
      queryResult: {} as ReportQueryResult,
    });
  };

  useEffect(() => {
    return () => {
      isReportPageChange();
    };
  }, []);

  const REPORT_EXPORTS = GetString('REPORT_EXPORTS');

  const onViewExportStatusClicked = () => {
    setReportExportsGridModalVisible(true);
  };

  const toggleDialog = () => {
    setReportExportsGridModalVisible(!ReportExportsGridModalVisible);
  };

  return (
    <React.Fragment>
      <div className={classes.reportsFilter}>
        <ReportsFilter
          setIsReportsPanelBarVisible={setReportsPanelBarVisibility}
          isReportsPage={true}
          returnToReportsList={returnToReportsList}
        />
      </div>
      <div>
        <div className={classes.headerControlsContainer}>
          <Button type="submit" onClick={() => onViewExportStatusClicked()}>
            {GetString('VIEW_REPORT_STATUS')}
          </Button>
        </div>
        {ReportExportsGridModalVisible && (
          <Dialog title={REPORT_EXPORTS} onClose={toggleDialog}>
            <ReportExportStatusesGrid />
          </Dialog>
        )}
      </div>
      <div className={classes.reportsList}>
        <div>
          <Breadcrumb
            data={breadcrumbData}
            onItemSelect={handleItemSelect}
            className={classes.header}
            breadcrumbDelimiter={NavigationNextIcon}
          />
        </div>
        {breadcrumbData.length === 1 ? (
          <ReportsList
            handleGenerateReportClick={handleGenerateReportClick}
            isReportsPanelBarVisible={isReportsPanelBarVisible}
          />
        ) : null}
        <Fade>
          <NotificationGroup className={classes.bottomRight}>
            {error && (
              <Notification
                className={classes.error}
                type={{
                  style: 'error',
                  icon: true,
                }}
                closable={true}
                onClose={() => clearFetchError()}
              >
                <StringResourceManager stringKey={fetchError.Message} />
                {`(${status}: ${fetchError.StatusCode})`}
              </Notification>
            )}
          </NotificationGroup>
        </Fade>
      </div>
    </React.Fragment>
  );
};

export default ReportsSelection;
